import { Injectable } from '@angular/core';
import { applyTransaction } from '@datorama/akita';
import { tap } from 'rxjs/operators';
import { CostCenterListParams } from 'src/app/core/models/client-models';
import { FilterService } from '../../../interfaces/filter/filter-service';
import { LocalStoreConfigModel } from '../../../models/local-store-config.model';
import { CostCenterBackendService } from '../../../services/cost-center/cost-center-backend.service';
import { CostCenterItemsStoreService } from '../../cost-center-items-store-service';
import { CostCenterListQuery } from '../../cost-center-list/cost-center-list.query';
import { CostCenterListService } from '../../cost-center-list/cost-center-list.service';
import { LocalStateKeys } from '../../local-state-keys';
import { OrderItemsStoreService } from '../../order-items-store-service';
import { SessionQuery } from '../../session/session.query';

@Injectable({
  providedIn: 'root',
})
export class CostCenterItemsService implements FilterService {
  constructor(
    private sessionQuery: SessionQuery,
    private costCenterListQuery: CostCenterListQuery,
    private costCenterListService: CostCenterListService,
    private orderItemsStoreService: OrderItemsStoreService,
    private costCenterStoreService: CostCenterItemsStoreService,
    private costCenterBackendService: CostCenterBackendService
  ) {}

  /**
   * Select next cost center list page.
   */
  getNextPage(orderStoreKey: LocalStateKeys, costCenterKey: LocalStateKeys) {
    const costCenterStore =
      this.costCenterStoreService.getOrCreateStore(costCenterKey);
    costCenterStore.store.setLoading(true);

    const orderStore =
      this.orderItemsStoreService.getOrCreateStore(orderStoreKey);
    // Get values for http params.
    const nextPage: number = costCenterStore.query.getValue().page + 1;
    this.setPage(nextPage, costCenterKey);
    const pageSize: number = costCenterStore.query.getValue().pageSize;
    const IdPerson: number = this.sessionQuery.getValue().user.id;

    const orderID: number = orderStore.query.getActive()?.orderID;
    const filterValue: string = this.costCenterListQuery.getValue().filterValue;

    const costCenterParams: CostCenterListParams = {
      pageNumber: nextPage,
      pageSize: pageSize,
      orderID: orderID,
      personID: IdPerson,
      filterValue: filterValue ? filterValue : '',
    } as CostCenterListParams;
    this.setFilter(costCenterParams);

    this.costCenterBackendService
      .getCostCenterList(costCenterParams)
      .pipe(tap(() => costCenterStore.store.setLoading(false)))
      .subscribe({
        next: (data) => {
          costCenterStore.store.upsertMany(data);
          costCenterStore.store.setError(null);
          if (data.length === 0) {
            // if no results set page to previous.
            this.setPage(nextPage - 1, costCenterKey);
            // set last page indicator.
            costCenterStore.store.update((state) => (state.lastPage = true));
          }
        },
        error: (error) => costCenterStore.store.setError(error.error),
      });
  }

  setFilter(params: CostCenterListParams) {
    return this.costCenterListService.setFilter(params);
  }

  setPage(newPage: number, costCenterKey: LocalStateKeys) {
    const costCenterStore =
      this.costCenterStoreService.getOrCreateStore(costCenterKey);
    costCenterStore.store.update((state) => ({
      ...state,
      page: newPage,
    }));
  }

  clearCache(costCenterKey: LocalStateKeys) {
    const costCenterStore =
      this.costCenterStoreService.getOrCreateStore(costCenterKey);
    applyTransaction(() => {
      costCenterStore.store.update((state) => ({
        ...state,
        page: 0,
        currentPage: 0,
        pageSize: 50,
        lastPage: false,
      }));
      costCenterStore.store.remove();
    });
  }

  clearFilterValue() {
    this.costCenterListService.clearFilterValue();
  }

  filterValueChange(
    filterValue: string,
    orderStoreKey: LocalStateKeys,
    costCenterStoreKey: LocalStateKeys
  ) {
    this.clearCache(costCenterStoreKey);
    this.costCenterListService.filterValueChange(filterValue);
    this.getNextPage(orderStoreKey, costCenterStoreKey);
  }

  /**
   * Set active cost center.
   *
   * @param id Of cost center.
   * @param costCenterKey
   */
  setActive(id: number, costCenterKey: LocalStateKeys) {
    const costCenterStore =
      this.costCenterStoreService.getOrCreateStore(costCenterKey);
    costCenterStore.store.setActive(id);
  }

  setFilterOptions(
    filterSelection: any,
    storeConfig?: LocalStoreConfigModel
  ): void {
    applyTransaction(() => {
      this.costCenterListService.setFilterOptions(filterSelection, storeConfig);
      this.clearCache(storeConfig.costCenterItems);
    });
    this.getNextPage(storeConfig.orderItems, storeConfig.costCenterItems);
  }
}
